import { useEffect, useRef, useState } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  /** True briefly after transitioning from offline → online */
  wasOffline: boolean;
}

/**
 * Tracks browser online/offline state via navigator.onLine and
 * the window 'online'/'offline' events. When the connection is
 * restored after being offline, `wasOffline` stays true for a
 * short period so the UI can show a "reconnected" message.
 */
export const useNetworkStatus = (reconnectedDurationMs = 3000): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setWasOffline(false), reconnectedDurationMs);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [reconnectedDurationMs]);

  return { isOnline, wasOffline };
};
